// Extracted from `pages/CustomerDetailPage.js` (P2 refactor step 2).
// Renders the "Detalle del servicio" modal — a self-contained editor for a
// single `customer_service` including the collapsible "Información Técnica
// Avanzada" section (loaded on demand, encrypted at rest).
//
// This is a 1:1 move of the JSX; no styling, class names, `data-testid`s or
// business rules were changed. All controlled state and mutating handlers
// stay on the parent (`CustomerDetailPage`) and are passed in as props so
// saving, dedup and refresh behaviour is preserved exactly as before.
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Save, Shield, Lock, ChevronDown } from "lucide-react";
import {
  splitTechnicalInfoByTemplate,
  resolveTechnicalTemplate,
  resolveTemplateLabel,
} from "../../lib/technicalFieldTemplates";
import {
  formatDateES,
  formatNumericValue,
  calculateDaysRemaining,
  calculateEndDate,
} from "./utils";

export const ServiceDetailDialog = ({
  open,
  onOpenChange,
  selectedService,
  setSelectedService,
  locations,
  catalogServices,
  serviceCategories,
  formatCurrency,
  canViewTechnicalInfo,
  techInfoExpanded,
  setTechInfoExpanded,
  loadTechnicalInfo,
  loadingTechInfo,
  technicalInfo,
  setTechnicalInfo,
  handleUpdateService,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-white font-heading">
            {selectedService ? selectedService.nombre_servicio : "Detalle del servicio"}
          </DialogTitle>
        </DialogHeader>
        {selectedService && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#03BEE4]">Estado</Label>
                <Select
                  value={selectedService.estado}
                  onValueChange={(v) => {
                    const updates = { estado: v };
                    if (v === "BAJA" && !selectedService.fecha_baja) {
                      updates.fecha_baja = new Date().toISOString().split("T")[0];
                    }
                    if (v !== "BAJA") {
                      updates.fecha_baja = null;
                    }
                    setSelectedService({ ...selectedService, ...updates });
                  }}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    <SelectItem value="ACTIVO" className="text-white">Activo</SelectItem>
                    <SelectItem value="PENDIENTE" className="text-white">Pendiente</SelectItem>
                    <SelectItem value="SUSPENDIDO" className="text-white">Suspendido</SelectItem>
                    <SelectItem value="BAJA" className="text-white">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedService.estado === "BAJA" && (
                <div>
                  <Label className="text-[#03BEE4]">Fecha de Baja</Label>
                  <Input
                    type="date"
                    value={selectedService.fecha_baja || ""}
                    onChange={(e) => setSelectedService({ ...selectedService, fecha_baja: e.target.value })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>
              )}
              <div>
                <Label className="text-[#03BEE4]">Sede</Label>
                <Select
                  value={selectedService.location_id}
                  onValueChange={(v) => {
                    const loc = locations.find(l => l.id === v);
                    setSelectedService({
                      ...selectedService,
                      location_id: v,
                      nombre_sede: loc?.nombre_sede || ""
                    });
                  }}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id} className="text-white">
                        {loc.nombre_sede}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Paquete selector - only show for manually created services */}
              {!selectedService.provision_id && (
              <div>
                <Label className="text-[#03BEE4]">Paquete</Label>
                <Select
                  value={selectedService.catalog_service_id || "custom"}
                  onValueChange={(v) => {
                    if (v === "custom") {
                      setSelectedService({ ...selectedService, catalog_service_id: null });
                    } else {
                      const catalogService = catalogServices.find(s => s.id === v);
                      if (catalogService) {
                        setSelectedService({
                          ...selectedService,
                          catalog_service_id: v,
                          nombre_servicio: catalogService.nombre,
                          proveedor_id: catalogService.proveedor_id || "",
                          proveedor_nombre: catalogService.proveedor_nombre || "",
                          precio_mensual: catalogService.precio_base,
                        });
                      }
                    }
                  }}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue placeholder="Selecciona paquete" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    <SelectItem value="custom" className="text-slate-400">
                      -- Servicio personalizado --
                    </SelectItem>
                    {catalogServices.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-white">
                        {s.nombre} - {formatCurrency(s.precio_base)} {s.proveedor_nombre ? `(${s.proveedor_nombre})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div>
                <Label className="text-[#03BEE4]">Proveedor</Label>
                <div className="mt-1 p-2 bg-[#1B1B4B] border border-[#1E1E5F] rounded-md text-white">
                  {selectedService.proveedor_nombre || selectedService.proveedor || "Sin proveedor asignado"}
                </div>
              </div>
              <div>
                <Label className="text-[#03BEE4]">Precio Mensual (€)</Label>
                <div className="mt-1 p-2 bg-[#1B1B4B] border border-[#1E1E5F] rounded-md text-white">
                  {formatCurrency(selectedService.precio_mensual)}
                  <span className="text-xs text-slate-400 ml-2">(precio base)</span>
                </div>
              </div>
              <div>
                <Label className="text-[#03BEE4]">Descuento (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formatNumericValue(selectedService.descuento_monto)}
                  onChange={(e) => setSelectedService({ ...selectedService, descuento_monto: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[#03BEE4]">Concepto de Descuento</Label>
                <Input
                  value={selectedService.descuento_concepto || ""}
                  onChange={(e) => setSelectedService({ ...selectedService, descuento_concepto: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-[#03BEE4]">Descuento desde</Label>
                <Input
                  type="date"
                  value={selectedService.descuento_desde || ""}
                  onChange={(e) => setSelectedService({ ...selectedService, descuento_desde: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-[#03BEE4]">Descuento hasta</Label>
                <Input
                  type="date"
                  value={selectedService.descuento_hasta || ""}
                  onChange={(e) => setSelectedService({ ...selectedService, descuento_hasta: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-[#03BEE4]">Días Restantes Descuento</Label>
                <div className="mt-1 p-2 bg-[#1B1B4B] border border-[#1E1E5F] rounded-md">
                  {selectedService.descuento_hasta
                    ? (calculateDaysRemaining(selectedService.descuento_hasta) !== null
                        ? <span className={calculateDaysRemaining(selectedService.descuento_hasta) <= 30
                            ? calculateDaysRemaining(selectedService.descuento_hasta) <= 7
                              ? "text-red-400 font-medium"
                              : "text-yellow-400"
                            : "text-[#42E695]"}>
                            {calculateDaysRemaining(selectedService.descuento_hasta)} días
                          </span>
                        : <span className="text-red-400">Expirado</span>)
                    : <span className="text-slate-500">No aplica</span>}
                </div>
              </div>
              <div>
                <Label className="text-[#03BEE4]">Precio Base (con IVA)</Label>
                <div className="text-white text-xl font-bold mt-1">
                  {formatCurrency(selectedService.precio_mensual || 0)}
                </div>
              </div>
              {(parseFloat(selectedService.descuento_monto) || 0) > 0 && (
                <div>
                  <Label className="text-red-400">Descuento aplicado</Label>
                  <div className="text-red-400 text-lg font-semibold mt-1">
                    −{formatCurrency(selectedService.descuento_monto || 0)}
                    {selectedService.descuento_concepto && (
                      <span className="text-xs text-slate-400 block">{selectedService.descuento_concepto}</span>
                    )}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-[#03BEE4]">Precio sin IVA</Label>
                <div className="text-slate-300 text-lg font-semibold mt-1">
                  {formatCurrency(((parseFloat(selectedService.precio_mensual) || 0) - (parseFloat(selectedService.descuento_monto) || 0)) / 1.21)}
                </div>
              </div>
              <div className="col-span-2 bg-[#1B1B4B] rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <Label className="text-slate-400 text-xs">IVA (21%)</Label>
                    <div className="text-yellow-400 font-semibold">
                      {formatCurrency(((parseFloat(selectedService.precio_mensual) || 0) - (parseFloat(selectedService.descuento_monto) || 0)) - (((parseFloat(selectedService.precio_mensual) || 0) - (parseFloat(selectedService.descuento_monto) || 0)) / 1.21))}
                    </div>
                  </div>
                  <div className="text-right">
                    <Label className="text-slate-400 text-xs">Precio Final</Label>
                    <div className="text-[#42E695] text-2xl font-bold">
                      {formatCurrency((parseFloat(selectedService.precio_mensual) || 0) - (parseFloat(selectedService.descuento_monto) || 0))}
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#03BEE4]">Fecha de Contratación</Label>
                <Input
                  type="date"
                  value={selectedService.fecha_contratacion || ""}
                  onChange={(e) => setSelectedService({ ...selectedService, fecha_contratacion: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-[#03BEE4]">Fecha de Alta (Instalación)</Label>
                <Input
                  type="date"
                  value={selectedService.fecha_alta || ""}
                  onChange={(e) => setSelectedService({ ...selectedService, fecha_alta: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-[#03BEE4]">Permanencia (meses)</Label>
                <Input
                  type="number"
                  value={formatNumericValue(selectedService.permanencia_meses)}
                  onChange={(e) => setSelectedService({ ...selectedService, permanencia_meses: e.target.value })}
                  placeholder="Sin permanencia"
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-[#03BEE4]">Fecha Fin Permanencia</Label>
                <div className="mt-1 p-2 bg-[#1B1B4B] border border-[#1E1E5F] rounded-md text-white">
                  {selectedService.fecha_alta && selectedService.permanencia_meses
                    ? formatDateES(calculateEndDate(selectedService.fecha_alta, selectedService.permanencia_meses))
                    : <span className="text-slate-500">Se calcula automáticamente</span>}
                </div>
              </div>
              <div>
                <Label className="text-[#03BEE4]">Días Restantes Permanencia</Label>
                <div className="mt-1 p-2 bg-[#1B1B4B] border border-[#1E1E5F] rounded-md">
                  {(() => {
                    if (!selectedService.fecha_alta || !selectedService.permanencia_meses) {
                      return <span className="text-slate-500">-</span>;
                    }
                    const fechaFin = calculateEndDate(selectedService.fecha_alta, selectedService.permanencia_meses);
                    const diasRestantes = Math.max(0, Math.ceil((new Date(fechaFin) - new Date()) / (1000 * 60 * 60 * 24)));
                    const colorClass = diasRestantes > 90 ? "text-purple-400" : diasRestantes > 30 ? "text-yellow-400" : "text-[#42E695]";
                    return (
                      <span className={`font-semibold ${colorClass}`}>
                        {diasRestantes} días
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div>
                <Label className="text-[#03BEE4]">Penalización base</Label>
                <div className="mt-1 p-2 bg-[#1B1B4B] border border-[#1E1E5F] rounded-md text-slate-300">
                  {formatCurrency(selectedService.penalizacion_cancelacion || 0)}
                </div>
              </div>
              {selectedService.estado === "BAJA" && selectedService.fecha_baja && selectedService.penalizacion_cancelacion > 0 && (
                <div className="col-span-2 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <Label className="text-orange-400 font-semibold">Penalización Proporcional a Aplicar</Label>
                  {(() => {
                    // Calculate proportional penalty
                    const fechaAlta = selectedService.fecha_alta ? new Date(selectedService.fecha_alta) : null;
                    const fechaBaja = selectedService.fecha_baja ? new Date(selectedService.fecha_baja) : new Date();
                    const fechaFinPermanencia = selectedService.fecha_fin_permanencia ? new Date(selectedService.fecha_fin_permanencia) : null;

                    if (!fechaAlta || !fechaFinPermanencia) {
                      return <p className="text-slate-400 mt-1">No se puede calcular sin fechas</p>;
                    }

                    const permanenciaTotal = selectedService.permanencia_meses || 12;
                    const penalizacionBase = selectedService.penalizacion_cancelacion || 0;

                    // Calculate months remaining from fecha_baja to fecha_fin_permanencia
                    const mesesTranscurridos = Math.max(0, Math.floor((fechaBaja - fechaAlta) / (1000 * 60 * 60 * 24 * 30)));
                    const mesesRestantes = Math.max(0, permanenciaTotal - mesesTranscurridos);

                    // Proportional penalty = (meses restantes / permanencia total) * penalizacion base
                    const penalizacionProporcional = mesesRestantes > 0
                      ? (mesesRestantes / permanenciaTotal) * penalizacionBase
                      : 0;

                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-slate-300 text-sm">
                          Meses de permanencia cumplidos: <span className="text-white font-medium">{mesesTranscurridos} de {permanenciaTotal}</span>
                        </p>
                        <p className="text-slate-300 text-sm">
                          Meses restantes: <span className="text-white font-medium">{mesesRestantes}</span>
                        </p>
                        <p className="text-slate-300 text-sm">
                          Cálculo: ({mesesRestantes} / {permanenciaTotal}) × {formatCurrency(penalizacionBase)}
                        </p>
                        <p className="text-orange-400 text-xl font-bold mt-2">
                          = {formatCurrency(penalizacionProporcional)}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Opportunity & Provision Info */}
              {(selectedService.opportunity_number || selectedService.provision_number) && (
                <div className="col-span-2 p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-xs text-slate-400 mb-2">Origen del Servicio</p>
                  <div className="flex gap-4">
                    {selectedService.opportunity_number && (
                      <Badge className="bg-purple-500/20 text-purple-400">
                        Oportunidad: {selectedService.opportunity_number}
                      </Badge>
                    )}
                    {selectedService.provision_number && (
                      <Badge className="bg-cyan-500/20 text-cyan-400">
                        Provisión: {selectedService.provision_number}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Technical Info Section - Only visible for TECNICO and SUPERADMIN */}
              {canViewTechnicalInfo() && (
                <div className="col-span-2">
                  <Collapsible
                    open={techInfoExpanded}
                    onOpenChange={(open) => {
                      setTechInfoExpanded(open);
                      if (open && selectedService) {
                        loadTechnicalInfo(selectedService.id);
                      }
                    }}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F] hover:bg-[#1B1B4B] cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-orange-400" />
                          <span className="text-white font-medium">Información Técnica Avanzada</span>
                          <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                            <Lock className="h-3 w-3 mr-1" />
                            Cifrado
                          </Badge>
                        </div>
                        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${techInfoExpanded ? "rotate-180" : ""}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-3 p-4 rounded-lg bg-[#0A0A35] border border-orange-500/30 space-y-4">
                        {loadingTechInfo ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                          </div>
                        ) : (() => {
                          // Prefer Catalog-driven category.campos_tecnicos;
                          // fall back to the built-in frontend templates
                          // when the category has no configured fields.
                          const template = resolveTechnicalTemplate(selectedService, catalogServices, serviceCategories);
                          const templateLabel = resolveTemplateLabel(selectedService, catalogServices, serviceCategories);
                          const { unknown: unknownKeys } = splitTechnicalInfoByTemplate(technicalInfo, template);
                          const updateKey = (key, value) => setTechnicalInfo(prev => ({ ...(prev || {}), [key]: value }));

                          const renderField = (field) => {
                            const val = technicalInfo[field.key];
                            const common = "mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white";
                            switch (field.type) {
                              case "textarea":
                                return (
                                  <Textarea
                                    value={val || ""}
                                    onChange={(e) => updateKey(field.key, e.target.value)}
                                    rows={3}
                                    className={common}
                                    data-testid={`tech-field-${field.key}`}
                                  />
                                );
                              case "password":
                                return (
                                  <Input
                                    type="password"
                                    value={val || ""}
                                    onChange={(e) => updateKey(field.key, e.target.value)}
                                    placeholder="••••••••"
                                    className={common}
                                    data-testid={`tech-field-${field.key}`}
                                  />
                                );
                              case "date":
                                return (
                                  <Input
                                    type="date"
                                    value={val || ""}
                                    onChange={(e) => updateKey(field.key, e.target.value)}
                                    className={common}
                                    data-testid={`tech-field-${field.key}`}
                                  />
                                );
                              case "boolean":
                                return (
                                  <div className="mt-2 flex items-center gap-2">
                                    <Switch
                                      checked={!!val}
                                      onCheckedChange={(checked) => updateKey(field.key, checked)}
                                      data-testid={`tech-field-${field.key}`}
                                    />
                                    <span className="text-sm text-slate-400">{val ? "Sí" : "No"}</span>
                                  </div>
                                );
                              case "select":
                                return (
                                  <Select
                                    value={val || ""}
                                    onValueChange={(v) => updateKey(field.key, v)}
                                  >
                                    <SelectTrigger className={common} data-testid={`tech-field-${field.key}`}>
                                      <SelectValue placeholder="Seleccionar" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                                      {(field.options || []).map(opt => (
                                        <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              default:
                                return (
                                  <Input
                                    value={val || ""}
                                    onChange={(e) => updateKey(field.key, e.target.value)}
                                    className={common}
                                    data-testid={`tech-field-${field.key}`}
                                  />
                                );
                            }
                          };

                          return (
                            <>
                              <div className="flex items-center justify-between pb-2 border-b border-[#1E1E5F]">
                                <span className="text-xs text-slate-400">Plantilla detectada:</span>
                                <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                                  {templateLabel}
                                </Badge>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {template.map(field => (
                                  <div key={field.key} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                                    <Label className="text-orange-400">{field.label}</Label>
                                    {renderField(field)}
                                  </div>
                                ))}
                              </div>

                              {Object.keys(unknownKeys).length > 0 && (
                                <div className="pt-3 mt-3 border-t border-[#1E1E5F]">
                                  <Label className="text-slate-300 text-sm">Otros datos técnicos</Label>
                                  <p className="text-xs text-slate-500 mb-2">Valores que no pertenecen a la plantilla activa. Se conservan al guardar.</p>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.entries(unknownKeys).map(([k, v]) => (
                                      <div key={k}>
                                        <Label className="text-slate-400 text-xs">{k}</Label>
                                        <Input
                                          value={typeof v === "string" ? v : JSON.stringify(v ?? "")}
                                          onChange={(e) => updateKey(k, e.target.value)}
                                          className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm"
                                          data-testid={`tech-unknown-${k}`}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <p className="text-xs text-slate-500 pt-2 border-t border-[#1E1E5F]">
                                Los cambios de información técnica se guardan junto al resto con el botón <span className="text-white font-medium">Guardar Cambios</span>.
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}

              <div className="col-span-2">
                <Label className="text-[#03BEE4]">Notas</Label>
                <Textarea
                  value={selectedService.notas || ""}
                  onChange={(e) => setSelectedService({ ...selectedService, notas: e.target.value })}
                  rows={3}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-[#1E1E5F] text-slate-300"
              >
                Cancelar
              </Button>
              <Button onClick={handleUpdateService} className="bg-[#03BEE4] text-white">
                <Save className="h-4 w-4 mr-2" />
                Guardar Cambios
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
